import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Chip,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ProductoInfo360Dialog from './info360/ProductoInfo360Dialog';
import ProductoInfo360ListButton from './info360/ProductoInfo360ListButton';
import ImprimirEtiquetasProductoHost from '../../components/inventario/ImprimirEtiquetasProductoHost';
import BarcodeIcon from '../../components/inventario/BarcodeIcon';
import ProveedorInlineSearchField from '../../components/ProveedorInlineSearchField';
import ProveedorSearch from '../../components/ProveedorSearch';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../utils/apiError';
import * as XLSX from 'xlsx';
import productoService from '../../services/productoService';
import type { Proveedor } from '../../services/proveedorService';
import { labelTipoArticulo } from '../../config/tipoArticuloOptions';
import { catalogosService } from '../../services/catalogosService';
import { ProductoFoto, getProductoFallbackImage, getProductoImageBySize } from '../../utils/productImage';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';
import { useGridColumns, type GridColumnDef } from '../../hooks/useGridColumns';
import {
  AccionesColumnHeader,
  PersonalizarColumnasDialog,
} from '../../components/shared/PersonalizarColumnasDialog';
import { descubrirColumnasDeItems, presentarCampoGrilla } from '../../utils/gridFieldValue';

interface Producto {
  codigoArticulo: string;
  descripcion: string;
  imagen?: string | null;
  fotos?: ProductoFoto[] | null;
  nombreLargo?: string;
  codBarra?: string;
  codigoProveedor?: string;
  codProveedor?: number | null;
  precioVenta?: number;
  cantidad?: number;
  stockMin?: number;
  stockMax?: number;
  costo?: number;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  nombreRamo?: string;
  nombreLinea?: string;
  nombreClase?: string;
  codigoRamoNavigation?: {
    nombreRamo?: string;
  } | null;
  codigoLineaNavigation?: {
    nombreLinea?: string;
  } | null;
  codigoClaseNavigation?: {
    nombreClase?: string;
  } | null;
  unidadMedida?: {
    idUnidadMedida: string;
    descripcion: string;
  };
  tipoArticulo?: string | null;
  coberturaDias?: number | null;
  demandaMensual6m?: number | null;
  leadTimeDias?: number | null;
}

const renderEstadoProductoChip = (tipoArticulo?: string | null) => {
  const tipo = (tipoArticulo ?? '').trim().toUpperCase();
  if (tipo === 'INA') {
    return <Chip label={labelTipoArticulo('INA')} color="error" size="small" />;
  }
  if (tipo === 'DIS') {
    return <Chip label={labelTipoArticulo('DIS')} color="warning" size="small" />;
  }
  return null;
};

interface ProductoPagedResult {
  items: Producto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

type ProductoColumnId =
  | 'codigo'
  | 'foto'
  | 'descripcion'
  | 'ramo'
  | 'linea'
  | 'clase'
  | 'estado'
  | 'precio'
  | 'stock'
  | 'acciones';

const PRODUCTOS_COLUMNAS: GridColumnDef<ProductoColumnId>[] = [
  { id: 'codigo', label: 'Código', sortable: true },
  { id: 'foto', label: 'Foto', sortable: false },
  { id: 'descripcion', label: 'Descripción', sortable: true },
  { id: 'ramo', label: 'Ramo', sortable: true },
  { id: 'linea', label: 'Línea', sortable: true },
  { id: 'clase', label: 'Clase', sortable: true },
  { id: 'estado', label: 'Estado', sortable: true },
  { id: 'precio', label: 'Precio', align: 'right', sortable: true },
  { id: 'stock', label: 'Stock', align: 'right', sortable: true },
  { id: 'acciones', label: 'Acciones', align: 'center', sortable: false, alwaysVisible: true },
];

const PRODUCTOS_EXPORT_PAGE_SIZE = 100;

const TIPOS_SIN_STOCK = new Set(['FIN', 'KIT', 'SER']);

const productoInformaStock = (tipoArticulo?: string | null): boolean =>
  !TIPOS_SIN_STOCK.has((tipoArticulo ?? '').trim().toUpperCase());

const formatStockProducto = (producto: Pick<Producto, 'tipoArticulo' | 'cantidad'>): string => {
  if (!productoInformaStock(producto.tipoArticulo)) return '-';
  return String(producto.cantidad ?? 0);
};

const COLUMN_ORDEN_API: Partial<Record<ProductoColumnId, string>> = {
  codigo: 'CodigoArticulo',
  descripcion: 'Descripcion',
  ramo: 'CodigoRamo',
  linea: 'CodigoLinea',
  clase: 'CodigoClase',
  estado: 'TipoArticulo',
  precio: 'PrecioVenta',
  stock: 'Cantidad',
};

const Productos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [productosData, setProductosData] = useState<ProductoPagedResult>({
    items: [],
    totalCount: 0,
    pageNumber: 0,
    pageSize: 10,
    totalPages: 0,
  });
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<any[]>([]);
  const [importFileName, setImportFileName] = useState<string>('');
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    exitosos: number;
    conErrores: number;
    mensajesError: string[];
  } | null>(null);
  const [importProgress, setImportProgress] = useState<{ procesados: number; total: number } | null>(null);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [info360Articulo, setInfo360Articulo] = useState<string | null>(null);
  const [etiquetasProducto, setEtiquetasProducto] = useState<{
    codigoArticulo: string;
    descripcion: string;
  } | null>(null);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [catalogoDescripciones, setCatalogoDescripciones] = useState<{
    ramos: Record<string, string>;
    lineas: Record<string, string>;
    clases: Record<string, string>;
  }>({
    ramos: {},
    lineas: {},
    clases: {},
  });
  const [orderBy, setOrderBy] = useState<ProductoColumnId | ''>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [stockDesde, setStockDesde] = useState('');
  const [stockHasta, setStockHasta] = useState('');
  const [codigoDesde, setCodigoDesde] = useState('');
  const [codigoHasta, setCodigoHasta] = useState('');
  const [codProveedorFiltro, setCodProveedorFiltro] = useState<number | null>(null);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [printPorProveedorLoading, setPrintPorProveedorLoading] = useState(false);
  const [pdfListadoUrl, setPdfListadoUrl] = useState<string | null>(null);
  const {
    columnasVisibles,
    columnasDefinidas,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
  } = useGridColumns({
    gridKey: 'productos',
    columns: PRODUCTOS_COLUMNAS,
  });
  const columnasAPI = [
    'codigoArticulo',
    'nombreLargo',
    'codBarra',
    'descripcion',
    'costo',
    'precioVenta',
    'codigoRamo',
    'codigoLinea',
    'codigoClase',
    'idUnidadMedida',
    'stockMin',
    'stockMax',
    'tipoArticulo',
    'calculaIva',
    'ivaRI',
    'ivaRN',
    'ivaCF',
    'ivaEX',
    'ivaMT',
    'impInterno1',
    'impInterno2',
    'tasaHidrica',
    'porc_Desc',
    'codigoProveedor',
    'codigoArticuloProveedor'
  ];
  const camposObligatorios = [
    'codigoArticulo','descripcion','codigoRamo','codigoLinea','codigoClase'
  ];
  const camposNumericos = [
    'costo','precioVenta','stockMin','stockMax','ivaRI','ivaRN','ivaCF','ivaEX','ivaMT','impInterno1','impInterno2','tasaHidrica','porc_Desc'
  ];

  const renderProductoImagen = (
    producto: Producto,
    size: 'thumbnail' | 'medium' | 'full' = 'thumbnail',
    dimension = 44,
  ) => {
    const fallbackSrc = getProductoFallbackImage();
    const initialSrc = getProductoImageBySize(producto, size) || fallbackSrc;

    return (
      <Box
        sx={{
          width: dimension,
          height: dimension,
          borderRadius: 1.5,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          bgcolor: '#f5f5f5',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          key={`${producto.codigoArticulo}-${initialSrc}`}
          src={initialSrc}
          alt={`Foto de ${producto.codigoArticulo}`}
          loading="lazy"
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            const target = event.currentTarget;
            target.onerror = null;
            target.src = fallbackSrc;
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
    );
  };

  const normalizarTexto = (valor?: string | null): string => (valor ?? '').trim();

  const getDescripcionRamo = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoRamo);
    return (
      normalizarTexto(producto.codigoRamoNavigation?.nombreRamo) ||
      normalizarTexto(producto.nombreRamo) ||
      (codigo ? normalizarTexto(catalogoDescripciones.ramos[codigo]) : '') ||
      '-'
    );
  };

  const getDescripcionLinea = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoLinea);
    return (
      normalizarTexto(producto.codigoLineaNavigation?.nombreLinea) ||
      normalizarTexto(producto.nombreLinea) ||
      (codigo ? normalizarTexto(catalogoDescripciones.lineas[codigo]) : '') ||
      '-'
    );
  };

  const getDescripcionClase = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoClase);
    return (
      normalizarTexto(producto.codigoClaseNavigation?.nombreClase) ||
      normalizarTexto(producto.nombreClase) ||
      (codigo ? normalizarTexto(catalogoDescripciones.clases[codigo]) : '') ||
      '-'
    );
  };

  const normalizarParaBusqueda = (valor: string): string =>
    valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const palabrasBusqueda = useMemo(
    () =>
      normalizarParaBusqueda(searchTerm)
        .split(/\s+/)
        .filter(Boolean),
    [searchTerm],
  );

  const searchTermApi = useMemo(() => palabrasBusqueda.join(' '), [palabrasBusqueda]);

  const handleRequestSort = (columnId: ProductoColumnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const limpiarFiltrosRango = () => {
    setStockDesde('');
    setStockHasta('');
    setCodigoDesde('');
    setCodigoHasta('');
    setCodProveedorFiltro(null);
    setNombreProveedorFiltro('');
    setPage(0);
  };

  const handleProveedorFiltroSelect = (proveedor: { nroProveedor: number; nombre?: string }) => {
    setCodProveedorFiltro(proveedor.nroProveedor);
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
    setPage(0);
  };

  const limpiarProveedorFiltro = () => {
    setCodProveedorFiltro(null);
    setNombreProveedorFiltro('');
    setPage(0);
  };

  const buildListParams = useCallback(
    (pageNumber: number, pageSize: number) => {
      const stockDesdeNum = stockDesde.trim() === '' ? undefined : Number(stockDesde);
      const stockHastaNum = stockHasta.trim() === '' ? undefined : Number(stockHasta);
      const ordenApi = orderBy ? COLUMN_ORDEN_API[orderBy] : undefined;
      return {
        pageNumber: pageNumber + 1,
        pageSize,
        searchTerm: searchTermApi || undefined,
        orden: ordenApi,
        direccionOrden: ordenApi ? order.toUpperCase() : undefined,
        stockDesde:
          stockDesdeNum != null && !Number.isNaN(stockDesdeNum) ? stockDesdeNum : undefined,
        stockHasta:
          stockHastaNum != null && !Number.isNaN(stockHastaNum) ? stockHastaNum : undefined,
        codigoDesde: codigoDesde.trim() || undefined,
        codigoHasta: codigoHasta.trim() || undefined,
        codProveedor: codProveedorFiltro ?? undefined,
      };
    },
    [
      codigoDesde,
      codigoHasta,
      codProveedorFiltro,
      order,
      orderBy,
      searchTermApi,
      stockDesde,
      stockHasta,
    ],
  );

  const fetchAllProductosConFiltros = useCallback(async (): Promise<Producto[]> => {
    let pageNum = 0;
    let totalPages = 1;
    const allItems: Producto[] = [];
    do {
      const response = await api.get<ProductoPagedResult>('/api/productos', {
        params: buildListParams(pageNum, PRODUCTOS_EXPORT_PAGE_SIZE),
      });
      allItems.push(...(response.data.items ?? []));
      totalPages = response.data.totalPages ?? 1;
      pageNum += 1;
    } while (pageNum < totalPages);
    return allItems;
  }, [buildListParams]);

  const fetchProductos = useCallback(
    async (pageNumber: number, pageSize: number) => {
      setLoadingProductos(true);
      try {
        const response = await api.get<ProductoPagedResult>('/api/productos', {
          params: buildListParams(pageNumber, pageSize),
        });
        const items: Producto[] = response.data.items ?? [];
        setProductosData({
          ...response.data,
          items,
          pageNumber: (response.data.pageNumber ?? 1) - 1,
        });
      } catch (error) {
        console.error('Error al cargar productos:', error);
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los productos.'), {
          variant: 'error',
        });
        setProductosData({
          items: [],
          totalCount: 0,
          pageNumber: 0,
          pageSize,
          totalPages: 0,
        });
      } finally {
        setLoadingProductos(false);
      }
    },
    [buildListParams, enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void fetchProductos(page, rowsPerPage);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchProductos, page, rowsPerPage]);

  const productosPagina = productosData.items;
  const columnasParaAgregar = useMemo(
    () => descubrirColumnasDeItems(productosPagina, gridColumns.columnOrder),
    [gridColumns.columnOrder, productosPagina],
  );
  const totalCount = productosData.totalCount;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalCount, rowsPerPage]);

  const codigosPaginaKey = useMemo(
    () => productosPagina.map((p) => p.codigoArticulo).join('|'),
    [productosPagina],
  );

  useEffect(() => {
    if (!codigosPaginaKey) return;
    let cancelled = false;
    const codigos = codigosPaginaKey.split('|').filter(Boolean);
    void productoService
      .getProductosInfo360Semaforo(codigos)
      .then((semaforos) => {
        if (cancelled) return;
        const byCodigo = new Map(
          semaforos.map((s) => [s.codigoArticulo.trim().toUpperCase(), s]),
        );
        setProductosData((prev) => {
          let changed = false;
          const nextItems = prev.items.map((item) => {
            const s = byCodigo.get(item.codigoArticulo.trim().toUpperCase());
            if (!s) return item;
            if (
              item.coberturaDias === s.coberturaDias &&
              item.demandaMensual6m === s.demandaMensual6m &&
              item.leadTimeDias === s.leadTimeDias
            ) {
              return item;
            }
            changed = true;
            return {
              ...item,
              coberturaDias: s.coberturaDias,
              demandaMensual6m: s.demandaMensual6m,
              leadTimeDias: s.leadTimeDias,
            };
          });
          return changed ? { ...prev, items: nextItems } : prev;
        });
      })
      .catch((semaforoError) => {
        console.error('Error al cargar semáforo Info 360:', semaforoError);
      });
    return () => {
      cancelled = true;
    };
  }, [codigosPaginaKey]);

  const renderCeldaProducto = (columnId: string, producto: Producto) => {
    switch (columnId) {
      case 'codigo':
        return <TableCell sx={{ fontWeight: 500 }}>{producto.codigoArticulo}</TableCell>;
      case 'foto':
        return <TableCell>{renderProductoImagen(producto, 'thumbnail', 40)}</TableCell>;
      case 'descripcion':
        return <TableCell>{producto.descripcion}</TableCell>;
      case 'ramo':
        return <TableCell>{getDescripcionRamo(producto)}</TableCell>;
      case 'linea':
        return <TableCell>{getDescripcionLinea(producto)}</TableCell>;
      case 'clase':
        return <TableCell>{getDescripcionClase(producto)}</TableCell>;
      case 'estado':
        return <TableCell>{renderEstadoProductoChip(producto.tipoArticulo) ?? '—'}</TableCell>;
      case 'precio':
        return (
          <TableCell align="right">${producto.precioVenta?.toFixed(2) || '0.00'}</TableCell>
        );
      case 'stock':
        return <TableCell align="right">{formatStockProducto(producto)}</TableCell>;
      case 'acciones':
        return (
          <TableCell align="center">
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <ProductoInfo360ListButton
                producto={producto}
                onClick={() => setInfo360Articulo(producto.codigoArticulo)}
                compact
              />
              <Tooltip title="Imprimir etiquetas">
                <IconButton
                  onClick={() => {
                    setEtiquetasProducto({
                      codigoArticulo: producto.codigoArticulo,
                      descripcion: producto.descripcion || '',
                    });
                    setEtiquetasOpen(true);
                  }}
                  size="small"
                  sx={{
                    bgcolor: '#00897b',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: '#00695c',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <BarcodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar producto">
                <IconButton
                  onClick={() =>
                    navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)
                  }
                  size="small"
                  sx={{
                    bgcolor: '#2196f3',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: '#1565c0',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copiar producto">
                <IconButton
                  aria-label={`Copiar producto ${producto.codigoArticulo}`}
                  onClick={() =>
                    navigate(`/inventario/productos/nuevo?copiarDe=${encodeURIComponent(producto.codigoArticulo)}`)
                  }
                  size="small"
                  sx={{
                    bgcolor: '#7b1fa2',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: '#4a148c',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar producto">
                <IconButton
                  onClick={() => handleDelete(producto.codigoArticulo)}
                  size="small"
                  sx={{
                    bgcolor: '#f44336',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: '#d32f2f',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        );
      default: {
        const campo = presentarCampoGrilla(producto, columnId);
        return (
          <TableCell key={columnId} align={campo.align}>
            {campo.texto}
          </TableCell>
        );
      }
    }
  };

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [ramosResp, lineasResp, clasesResp] = await Promise.all([
          catalogosService.getRamos(1, 1000, ''),
          catalogosService.getLineas(1, 1000, ''),
          catalogosService.getClases(1, 1000, ''),
        ]);

        const ramos = Object.fromEntries(
          (ramosResp.items || [])
            .map((ramo) => [normalizarTexto(ramo.codigoRamo), normalizarTexto(ramo.nombreRamo)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        const lineas = Object.fromEntries(
          (lineasResp.items || [])
            .map((linea) => [normalizarTexto(linea.codigoLinea), normalizarTexto(linea.nombreLinea)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        const clases = Object.fromEntries(
          (clasesResp.items || [])
            .map((clase) => [normalizarTexto(clase.codigoClase), normalizarTexto(clase.nombreClase)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        setCatalogoDescripciones({ ramos, lineas, clases });
      } catch (error) {
        console.error('Error al cargar descripciones de catálogos:', error);
      }
    };

    cargarCatalogos();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (codigo: string) => {
    try {
      const checkRes = await api.get<{ canDelete: boolean; motivos?: string[]; message?: string }>(
        `/api/productos/${encodeURIComponent(codigo)}/puede-eliminar`
      );
      const check = checkRes.data;
      if (!check.canDelete) {
        enqueueSnackbar(check.message || check.motivos?.[0] || 'No se puede eliminar el producto.', { variant: 'error' });
        return;
      }
      if (!window.confirm('¿Está seguro de eliminar este producto?')) return;
      await api.delete(`/api/productos/${codigo}`);
      enqueueSnackbar('Producto eliminado', { variant: 'success' });
      await fetchProductos(page, rowsPerPage);
    } catch (error: unknown) {
      console.error('Error al eliminar producto:', error);
      const axiosError = error as { response?: { data?: { message?: string; motivos?: string[] } } };
      const msg = axiosError?.response?.data?.message
        || axiosError?.response?.data?.motivos?.[0]
        || 'No se puede eliminar el producto.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleExportExcelTabla = async () => {
    setExportExcelLoading(true);
    try {
      const allItems = await fetchAllProductosConFiltros();

      if (allItems.length === 0) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
        return;
      }

      const rows = allItems.map((p) => ({
        Código: p.codigoArticulo,
        Descripción: p.descripcion,
        Ramo: getDescripcionRamo(p),
        Línea: getDescripcionLinea(p),
        Clase: getDescripcionClase(p),
        Precio: p.precioVenta ?? 0,
        Stock: formatStockProducto(p),
        'Cód. proveedor': p.codProveedor ?? '',
        'Código de proveedor': p.codigoProveedor ?? '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Productos_${stamp}.xlsx`);
      enqueueSnackbar('Archivo Excel generado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar productos a Excel:', error);
      enqueueSnackbar('Error al exportar a Excel.', { variant: 'error' });
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleImprimirPorProveedor = async () => {
    if (codProveedorFiltro == null) {
      enqueueSnackbar('Seleccioná un proveedor para imprimir el listado.', { variant: 'warning' });
      return;
    }
    setPrintPorProveedorLoading(true);
    try {
      const stockDesdeNum = stockDesde.trim() === '' ? undefined : Number(stockDesde);
      const stockHastaNum = stockHasta.trim() === '' ? undefined : Number(stockHasta);
      const ordenApi = orderBy ? COLUMN_ORDEN_API[orderBy] : undefined;
      const blob = await productoService.getListadoPorProveedorPdf({
        codProveedor: codProveedorFiltro,
        searchTerm: searchTermApi || undefined,
        stockDesde:
          stockDesdeNum != null && !Number.isNaN(stockDesdeNum) ? stockDesdeNum : undefined,
        stockHasta:
          stockHastaNum != null && !Number.isNaN(stockHastaNum) ? stockHastaNum : undefined,
        codigoDesde: codigoDesde.trim() || undefined,
        codigoHasta: codigoHasta.trim() || undefined,
        orden: ordenApi,
        direccionOrden: ordenApi ? order.toUpperCase() : undefined,
      });
      setPdfListadoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (error) {
      console.error('Error al imprimir productos por proveedor:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el listado PDF.'), {
        variant: 'error',
      });
    } finally {
      setPrintPorProveedorLoading(false);
    }
  };

  // Plantilla Excel fiel exactamente al modelo de la API:
  const handleDescargarPlantilla = () => {
    // Lista exacta según tu API:
    const columnasAPI = [
      'codigoArticulo',
      'nombreLargo',
      'codBarra',
      'descripcion',
      'costo',
      'precioVenta',
      'codigoRamo',
      'codigoLinea',
      'codigoClase',
      'idUnidadMedida',
      'stockMin',
      'stockMax',
      'tipoArticulo',
      'calculaIva',
      'ivaRI',
      'ivaRN',
      'ivaCF',
      'ivaEX',
      'ivaMT',
      'impInterno1',
      'impInterno2',
      'tasaHidrica',
      'porc_Desc',
      'codigoProveedor',
      'codigoArticuloProveedor'
    ];
    try {
      const data = [columnasAPI, new Array(columnasAPI.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['codigoArticulo', 'Código único del artículo', 'Texto alfanumérico (obligatorio)'],
        ['nombreLargo', 'Nombre largo', 'Texto (opcional)'],
        ['codBarra', 'Código de barras', 'Único: no puede repetirse en otro producto (opcional)'],
        ['descripcion', 'Descripción', 'Texto (obligatorio)'],
        ['costo', 'Costo', 'Número (opcional)'],
        ['precioVenta', 'Precio de venta', 'Número (opcional)'],
        ['codigoRamo', 'Ramo', 'Código ramo existente (obligatorio)'],
        ['codigoLinea', 'Línea', 'Código línea existente (obligatorio)'],
        ['codigoClase', 'Clase', 'Código clase existente (obligatorio)'],
        ['idUnidadMedida', 'Unidad de medida', 'ID de unidad existente (obligatorio). Debe existir en el maestro de unidades de medida'],
        ['stockMin', 'Stock mínimo', 'Número (opcional)'],
        ['stockMax', 'Stock máximo', 'Número (opcional)'],
        ['tipoArticulo', 'Tipo de artículo', 'Texto (opcional)'],
        ['calculaIva', 'Calcula IVA', 'Texto (opcional)'],
        ['ivaRI', 'IVA Responsable Inscripto', 'Número (opcional, ej: 21)'],
        ['ivaRN', 'IVA RN', 'Número (opcional)'],
        ['ivaCF', 'IVA CF', 'Número (opcional)'],
        ['ivaEX', 'IVA Exento', 'Número (opcional)'],
        ['ivaMT', 'IVA Monotributo', 'Número (opcional)'],
        ['impInterno1', 'Impuesto interno 1', 'Número (opcional)'],
        ['impInterno2', 'Impuesto interno 2', 'Número (opcional)'],
        ['tasaHidrica', 'Tasa hídrica', 'Número (opcional)'],
        ['porc_Desc', 'Porcentaje descuento', 'Número (opcional)'],
        ['codigoProveedor', 'Proveedor', 'Código proveedor existente (opcional)'],
        ['codigoArticuloProveedor', 'Código artículo proveedor', 'Texto (opcional)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      const fileName = 'PlantillaProductos.xlsx';
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', {
        variant: 'success', autoHideDuration: 4000
      });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', {
        variant: 'error', autoHideDuration: 4000
      });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      if (!rows.length) {
        setImportPreviewRows([]);
        setImportFileName('');
        setImportResult(null);
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }
      // Normalizar cabeceras conocidas (alias) antes de guardar la vista previa
      const headerAliases: Record<string, string> = { codProveedor: 'codigoProveedor' };
      const normalizedRows = rows.map((r) => {
        const copy: any = { ...r };
        Object.entries(headerAliases).forEach(([from, to]) => {
          if (copy[from] !== undefined && copy[to] === undefined) {
            copy[to] = copy[from];
          }
        });
        return copy;
      });
      setImportPreviewRows(normalizedRows);
      setImportFileName(file.name);
      setImportResult(null);
      enqueueSnackbar('Archivo leído correctamente. Revisa y presiona "Importar" para procesar.', { variant: 'info', autoHideDuration: 4000 });
    } catch (error) {
      setImportPreviewRows([]);
      setImportFileName('');
      setImportResult(null);
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  const handleProcesarImportacionProductos = async () => {
    if (!importPreviewRows.length) return;
    try {
      setIsProcessingImport(true);
      setImportProgress(null);

      // Cargar catálogo de unidades de medida para validar idUnidadMedida
      let unidadesValidas = new Set<string>();
      try {
        const unidadesRes = await api.get('/api/catalogos/unidades-medida', {
          params: { pageNumber: 1, pageSize: 1000 },
        });
        const unidades = Array.isArray(unidadesRes.data?.items) ? unidadesRes.data.items : [];
        unidadesValidas = new Set(
          unidades
            .map((u: any) => u?.idUnidadMedida)
            .filter((v: any) => v != null && v !== '')
            .map((v: any) => String(v).trim().toUpperCase())
        );
      } catch (e) {
        // Si falla el catálogo, seguimos pero marcamos error en cada fila que use unidad
        console.error('Error al cargar catálogo de unidades de medida para importación:', e);
      }

      const rows = importPreviewRows;
      const headers = Object.keys(rows[0] ?? {});
      const noEncontrados = columnasAPI.filter(h => !headers.includes(h));
      if (noEncontrados.length > 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: rows.length,
          mensajesError: [`Columnas faltantes en el archivo: ${noEncontrados.join(', ')}`],
        });
        return;
      }

      let fallos = 0;
      const mensajesError: string[] = [];
      const validPayloads: { payload: any; rowIndex: number }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const filaExcel = i + 2; // considerando encabezado en fila 1

        // 1. Validar obligatorios
        const faltanCampos: string[] = [];
        for (const campo of camposObligatorios) {
          const valor = row[campo];
          if (valor === undefined || valor === null || String(valor).toString().trim() === '') {
            faltanCampos.push(campo);
          }
        }
        if (faltanCampos.length > 0) {
          mensajesError.push(`Fila ${filaExcel}: Falta completar campos obligatorios: ${faltanCampos.join(', ')}`);
          fallos++;
          continue;
        }

        // 2. Validar unidad de medida (idUnidadMedida)
        const rawUm = row['idUnidadMedida'];
        const umTexto = (rawUm !== undefined && rawUm !== null) ? String(rawUm).trim() : '';
        if (!umTexto) {
          mensajesError.push(`Fila ${filaExcel}: El campo obligatorio "idUnidadMedida" (unidad de medida) está vacío.`);
          fallos++;
          continue;
        }
        const umNormalizado = umTexto.toUpperCase();
        if (unidadesValidas.size > 0 && !unidadesValidas.has(umNormalizado)) {
          mensajesError.push(`Fila ${filaExcel}: La unidad de medida "${umTexto}" no existe en el maestro de unidades.`);
          fallos++;
          continue;
        }

        // 3. Validación de tipo numérico
        const camposTipoMal: string[] = [];
        for (const nCampo of camposNumericos) {
          const raw = row[nCampo];
          if (raw !== '' && raw !== undefined && raw !== null && isNaN(Number(raw))) {
            camposTipoMal.push(nCampo);
          }
        }
        if (camposTipoMal.length > 0) {
          mensajesError.push(`Fila ${filaExcel}: campos inválidos: ${camposTipoMal.join(', ')} (deben ser numéricos)`);
          fallos++;
          continue;
        }

        // 4. Mapear datos para API (CreateProductoDto)
        const producto: any = {};
        columnasAPI.forEach((key) => {
          const raw = row[key];
          if (camposNumericos.includes(key)) {
            producto[key] = (raw !== '' && raw !== undefined && raw !== null) ? Number(raw) : null;
          } else if (key === 'idUnidadMedida') {
            // Ya validado arriba; usamos la versión normalizada
            producto[key] = umNormalizado;
          } else if (
            key.startsWith('codigo') ||
            key.startsWith('id') ||
            key === 'tipoArticulo' ||
            typeof raw === 'string'
          ) {
            producto[key] = (raw !== '' && raw !== undefined && raw !== null) ? String(raw) : '';
          } else {
            producto[key] = (raw !== '' && raw !== undefined) ? raw : null;
          }
        });

        // Usar la misma unidad como UM de stock por defecto si no viene otra
        if (!producto.umrefStock) {
          producto.umrefStock = umNormalizado;
        }

        // Si viene Costo en el Excel y no se especifica CostoCatalogo,
        // usarlo también como costoCatalogo para que funcione la fórmula de precios.
        if (producto.costo != null && producto.costo !== '' &&
            (producto.costoCatalogo === undefined || producto.costoCatalogo === null || producto.costoCatalogo === '')) {
          producto.costoCatalogo = Number(producto.costo);
        }

        // Lógica de precio neto y porcentaje de ganancia:
        // - Si precioVenta viene vacío: usar costo como precioVenta y porcentajeGanan = 0
        // - Si precioVenta viene con valor y costo > 0: calcular porcentajeGanan
        const costoVal = producto.costo != null && producto.costo !== '' ? Number(producto.costo) : null;
        const tienePrecio = producto.precioVenta != null && producto.precioVenta !== '' && !isNaN(Number(producto.precioVenta));

        if (!tienePrecio && costoVal != null && !isNaN(costoVal)) {
          // Caso 1: no viene precioVenta, usar costo como precio neto
          producto.precioVenta = costoVal;
          producto.porcentajeGanan = 0;
        } else if (tienePrecio && costoVal != null && !isNaN(costoVal) && costoVal > 0) {
          // Caso 2: viene precioVenta, calcular porcentaje de ganancia en base a costo
          const pn = Number(producto.precioVenta);
          const pct = ((pn / costoVal) - 1) * 100;
          producto.porcentajeGanan = Math.round(pct * 100) / 100; // dos decimales
        }

        validPayloads.push({ payload: producto, rowIndex: filaExcel });
      }

      const barcodeFilas = new Map<string, number>();
      const sinDuplicadosArchivo: { payload: any; rowIndex: number }[] = [];
      for (const item of validPayloads) {
        const cb = String(item.payload.codBarra || '').trim();
        if (!cb) {
          sinDuplicadosArchivo.push(item);
          continue;
        }
        const key = cb.toUpperCase();
        const filaPrevia = barcodeFilas.get(key);
        if (filaPrevia != null) {
          mensajesError.push(
            `Fila ${item.rowIndex}: el código de barras "${cb}" ya está en la fila ${filaPrevia}. No puede repetirse.`,
          );
          fallos++;
          continue;
        }
        barcodeFilas.set(key, item.rowIndex);
        sinDuplicadosArchivo.push(item);
      }

      const payloadsUnicos: { payload: any; rowIndex: number }[] = [];
      for (const item of sinDuplicadosArchivo) {
        const cb = String(item.payload.codBarra || '').trim();
        if (!cb) {
          payloadsUnicos.push(item);
          continue;
        }
        try {
          const otro = await productoService.codigoBarraEnUsoPorOtro(cb, item.payload.codigoArticulo);
          if (otro) {
            mensajesError.push(
              `Fila ${item.rowIndex}: el código de barras "${cb}" ya está en el producto ${otro.codigoArticulo}.`,
            );
            fallos++;
            continue;
          }
        } catch {
          /* si no se puede consultar, el alta puede fallar en el servidor */
        }
        payloadsUnicos.push(item);
      }

      if (payloadsUnicos.length === 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: fallos,
          mensajesError,
        });
        return;
      }

      setImportProgress({ procesados: 0, total: payloadsUnicos.length });
      let exitos = 0;
      const batchSize = 100;

      for (let start = 0; start < payloadsUnicos.length; start += batchSize) {
        const chunk = payloadsUnicos.slice(start, start + batchSize);
        const payloads = chunk.map((x) => x.payload);
        try {
          const res = await productoService.createProductosBulk(payloads);
          exitos += res.exitosos;
          fallos += res.fallidos;
          res.errores.forEach((e) => {
            const rowIndex = chunk[e.indice]?.rowIndex ?? (start + e.indice + 2);
            mensajesError.push(`Fila ${rowIndex} (artículo ${e.codigoArticulo}): ${e.mensaje}`);
          });
        } catch (err: any) {
          const data = err?.response?.data;
          const msgBase = data?.message || err?.message || 'Error al procesar el lote';
          mensajesError.push(
            `Lote filas ${chunk[0]?.rowIndex ?? start + 2}-${chunk[chunk.length - 1]?.rowIndex ?? start + batchSize + 1}: ${String(msgBase)}`
          );
          fallos += chunk.length;
        }
        setImportProgress({
          procesados: Math.min(start + batchSize, payloadsUnicos.length),
          total: payloadsUnicos.length,
        });
      }

      setImportResult({
        total: rows.length,
        exitosos: exitos,
        conErrores: fallos,
        mensajesError,
      });
      fetchProductos(page, rowsPerPage);
    } finally {
      setIsProcessingImport(false);
      setImportProgress(null);
    }
  };

  return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con controles */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Productos
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/productos/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Producto'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {isMobile ? 'Importar' : 'Importar productos'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcelTabla()}
                disabled={exportExcelLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportExcelLoading ? 'Exportando...' : 'Exportar Excel'}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                label="Buscar"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFiltrosAbiertos((v) => !v)}
                  sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                >
                  Filtros
                </Button>
              </Stack>
            </Stack>
            <Collapse in={filtrosAbiertos}>
              <Box
                sx={{
                  mt: 2,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: '#fafafa',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Stock desde"
                      size="small"
                      type="number"
                      value={stockDesde}
                      onChange={(e) => {
                        setStockDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Stock hasta"
                      size="small"
                      type="number"
                      value={stockHasta}
                      onChange={(e) => {
                        setStockHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Código desde"
                      size="small"
                      value={codigoDesde}
                      onChange={(e) => {
                        setCodigoDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Código hasta"
                      size="small"
                      value={codigoHasta}
                      onChange={(e) => {
                        setCodigoHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} lg={4}>
                    <ProveedorInlineSearchField
                      label="Proveedor"
                      placeholder={isMobile ? 'Nro, nombre o CUIT' : 'Número, nombre o CUIT'}
                      helperText=""
                      size="small"
                      displayValue={
                        codProveedorFiltro != null
                          ? `${nombreProveedorFiltro || 'Proveedor'} - ${codProveedorFiltro}`
                          : ''
                      }
                      onSelect={handleProveedorFiltroSelect}
                      onOpenModal={() => setProveedorSearchOpen(true)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2} lg={2}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {codProveedorFiltro != null && (
                          <IconButton
                            size="small"
                            onClick={limpiarProveedorFiltro}
                            title="Quitar proveedor"
                            aria-label="Quitar proveedor"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Tooltip title="Imprimir por proveedor">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => void handleImprimirPorProveedor()}
                              disabled={
                                codProveedorFiltro == null ||
                                printPorProveedorLoading ||
                                loadingProductos
                              }
                              aria-label="Imprimir por proveedor"
                            >
                              {printPorProveedorLoading ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Button
                        variant="text"
                        size="small"
                        onClick={limpiarFiltrosRango}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', ml: 'auto', pr: 0 }}
                      >
                        {isMobile ? 'Limpiar' : 'Limpiar filtros'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {productosPagina.map((producto) => {
                const estadoChip = renderEstadoProductoChip(producto.tipoArticulo);
                return (
                <Card 
                  key={producto.codigoArticulo}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderProductoImagen(producto, 'thumbnail', 56)}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                            {producto.descripcion}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong> {producto.codigoArticulo}
                          </Typography>
                          {estadoChip && (
                            <Box sx={{ mt: 0.5 }}>{estadoChip}</Box>
                          )}
                        </Box>
                      </Stack>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ramo:</strong> {getDescripcionRamo(producto)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Línea:</strong> {getDescripcionLinea(producto)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Clase:</strong> {getDescripcionClase(producto)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Precio:</strong> ${producto.precioVenta?.toFixed(2) || '0.00'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: !productoInformaStock(producto.tipoArticulo)
                              ? 'text.secondary'
                              : (producto.cantidad || 0) > 0
                                ? 'primary.main'
                                : 'error.main',
                          }}
                        >
                          <strong>Stock:</strong> {formatStockProducto(producto)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <ProductoInfo360ListButton
                          producto={producto}
                          onClick={() => setInfo360Articulo(producto.codigoArticulo)}
                        />
                        <Tooltip title="Imprimir etiquetas">
                          <IconButton
                            onClick={() => {
                              setEtiquetasProducto({
                                codigoArticulo: producto.codigoArticulo,
                                descripcion: producto.descripcion || '',
                              });
                              setEtiquetasOpen(true);
                            }}
                            size="small"
                            sx={{
                              bgcolor: '#00897b',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#00695c' }
                            }}
                          >
                            <BarcodeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar producto">
                          <IconButton
                            onClick={() => navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1565c0' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copiar producto">
                          <IconButton
                            aria-label={`Copiar producto ${producto.codigoArticulo}`}
                            onClick={() =>
                              navigate(`/inventario/productos/nuevo?copiarDe=${encodeURIComponent(producto.codigoArticulo)}`)
                            }
                            size="small"
                            sx={{
                              bgcolor: '#7b1fa2',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#4a148c' }
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar producto">
                          <IconButton
                            onClick={() => handleDelete(producto.codigoArticulo)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
              })}
              {loadingProductos && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {!loadingProductos && productosPagina.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay productos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {columnasVisibles.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sx={{ fontWeight: 600 }}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        {col.id === 'acciones' ? (
                          <AccionesColumnHeader
                            label={col.label}
                            onCustomize={(event) => setColumnasAnchorEl(event.currentTarget)}
                          />
                        ) : col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : 'asc'}
                            onClick={() => handleRequestSort(col.id as ProductoColumnId)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingProductos ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnasVisibles.length, 1)} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : productosPagina.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnasVisibles.length, 1)} align="center" sx={{ py: 4 }}>
                        No hay productos para mostrar
                      </TableCell>
                    </TableRow>
                  ) : (
                    productosPagina.map((producto) => (
                      <TableRow
                        key={producto.codigoArticulo}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        {columnasVisibles.map((col) => (
                          <React.Fragment key={col.id}>
                            {renderCeldaProducto(col.id, producto)}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <PersonalizarColumnasDialog
        anchorEl={columnasAnchorEl}
        onClose={() => setColumnasAnchorEl(null)}
        columns={columnasDefinidas}
        columnOrder={gridColumns.columnOrder}
        visibleIds={gridColumns.visibleIds}
        onToggle={toggleColumnaVisible}
        onMove={moverColumna}
        onReorder={reordenarColumnas}
        onReset={restablecerColumnas}
        addableColumns={columnasParaAgregar}
        onAdd={agregarColumna}
      />

      {/* Modal de importación */}
      <Dialog open={openImportar} onClose={() => setOpenImportar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Productos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Bloque 1: Descargar plantilla */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar productos.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Bloque 2: Importar desde archivo */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar los productos.
              </Typography>
              <label htmlFor="excel-upload-productos">
                <input
                  id="excel-upload-productos"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChange}
                />
                <Button variant="contained" fullWidth color="primary" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
              {importFileName && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {importFileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!importPreviewRows.length || isProcessingImport}
                    onClick={handleProcesarImportacionProductos}
                    startIcon={isProcessingImport ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    sx={{ fontWeight: 700 }}
                  >
                    {isProcessingImport
                      ? (importProgress ? `Procesando ${importProgress.procesados} / ${importProgress.total}` : 'Procesando...')
                      : 'Importar'}
                  </Button>
                </Box>
              )}
              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Resultados de la Importación
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: importResult.conErrores > 0 ? '#ffebee' : '#ffedd5',
                      border: '1px solid',
                      borderColor: importResult.conErrores > 0 ? '#ffcdd2' : '#fed7aa',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Total procesados:</strong> {importResult.total}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Exitosos:</strong> {importResult.exitosos}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Con errores:</strong> {importResult.conErrores}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tasa de éxito:</strong>{' '}
                      {importResult.total > 0
                        ? `${Math.round((importResult.exitosos / importResult.total) * 100)}%`
                        : '0%'}
                    </Typography>
                  </Box>
                  {importResult.mensajesError.length > 0 && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #ffe0b2',
                        bgcolor: '#fff3e0',
                        p: 2,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Detalles de errores:
                      </Typography>
                      {importResult.mensajesError.map((m, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ mb: 0.5, whiteSpace: 'pre-line' }}
                        >
                          {m}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {importResult ? (
            <Button onClick={() => setOpenImportar(false)} variant="contained" color="primary">
              Finalizar
            </Button>
          ) : (
            <Button onClick={() => setOpenImportar(false)} color="inherit">
              Cancelar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ProductoInfo360Dialog
        open={info360Articulo != null}
        codigoArticulo={info360Articulo}
        onClose={() => setInfo360Articulo(null)}
      />
      {etiquetasProducto ? (
        <ImprimirEtiquetasProductoHost
          open={etiquetasOpen}
          codigoArticulo={etiquetasProducto.codigoArticulo}
          descripcion={etiquetasProducto.descripcion}
          onClose={() => setEtiquetasOpen(false)}
        />
      ) : null}
      <ProveedorSearch
        open={proveedorSearchOpen}
        onClose={() => setProveedorSearchOpen(false)}
        onSelect={(proveedor: Proveedor) => {
          handleProveedorFiltroSelect({
            nroProveedor: proveedor.nroProveedor,
            nombre: proveedor.nombre,
          });
          setProveedorSearchOpen(false);
        }}
      />
      {pdfListadoUrl ? (
        <PDFViewer
          pdfUrl={pdfListadoUrl}
          title={
            nombreProveedorFiltro
              ? `Productos — ${nombreProveedorFiltro}`
              : `Productos — proveedor #${codProveedorFiltro}`
          }
          documentId={`productos-proveedor-${codProveedorFiltro ?? 0}`}
          documentType="informeCliente"
          downloadFileName={`Productos_proveedor_${codProveedorFiltro ?? 'sin'}_${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`}
          onClose={() => {
            setPdfListadoUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
          }}
        />
      ) : null}
      </Box>
  );
};

export default Productos;